import React from "react";
import { useForm } from "react-hook-form";
import { X } from "lucide-react";
import {
  Country,
  CreateCountryRequest,
  UpdateCountryRequest,
} from "../../api/services/countries/type";
import CustomInput from "../ui/CustomInput";
import PrimaryButton from "../ui/PrimaryButton";
import { ReactComponent as GlobeIcon } from "../../assets/ic_globe.svg";
import { ReactComponent as PhoneIcon } from "../../assets/ic_phone.svg";

interface CountryFormProps {
  country?: Country;
  isLoading?: boolean;
  onSubmit: (data: CreateCountryRequest | UpdateCountryRequest) => void;
  onCancel: () => void;
}

export const CountryForm: React.FC<CountryFormProps> = ({
  country,
  isLoading = false,
  onSubmit,
  onCancel,
}) => {
  const isEditMode = !!country;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<CreateCountryRequest>({
    defaultValues: {
      name: country?.name || "",
      callingCode: country?.callingCode || "",
    },
  });

  React.useEffect(() => {
    if (country) {
      setValue("name", country.name);
      setValue("callingCode", country.callingCode);
    } else {
      reset();
    }
  }, [country, setValue, reset]);

  const onFormSubmit = (data: CreateCountryRequest) => {
    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-slate-800 p-6 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">
            {isEditMode ? "Edit Country" : "Add New Country"}
          </h2>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-white transition-colors"
            disabled={isLoading}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6 space-y-6">
          <div className="space-y-4">
            <CustomInput
              label="Country Name"
              type="text"
              placeholder="Enter country name"
              register={register}
              name="name"
              validation={{
                required: "Country name is required",
                validate: (v: string) => {
                  const trimmed = v.trim();
                  if (trimmed.length === 0) return "Country name is required";
                  if (trimmed.length < 2)
                    return "Country name must be at least 2 characters";
                  if (!/^[A-Za-z\s]+$/.test(trimmed))
                    return "Country name must contain letters and spaces only";
                },
              }}
              error={errors.name}
              disabled={isLoading}
              icon={<GlobeIcon className="w-5 h-5 text-slate-400" />}
            />

            <CustomInput
              label="Calling Code"
              type="text"
              placeholder="e.g., +1, +977, +91"
              register={register}
              name="callingCode"
              validation={{
                required: "Calling code is required",
                pattern: {
                  value: /^\+\d{1,4}$/,
                  message:
                    "Calling code must start with + followed by 1-4 digits",
                },
              }}
              error={errors.callingCode}
              disabled={isLoading}
              icon={<PhoneIcon className="w-5 h-5 text-slate-400" />}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <PrimaryButton
              type="submit"
              isLoading={isLoading}
              loadingText={isEditMode ? "Updating..." : "Creating..."}
              className="flex-1"
            >
              {isEditMode ? "Update Country" : "Create Country"}
            </PrimaryButton>
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 hover:border-slate-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
